<script>
    import { apiUrl } from "@/utils";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import ProductCardButtons from "@/components/ProductCardButtons";
    import ProductPrice from "@allugears/components/ProductPrice";

    export default {
        name: "ProductCard",
        components: {
            ProductPrice,
            ProductCardButtons,
        },
        props: {
            product: Object,
            position: Number,
            isSmall: Boolean,
            inCartList: Array,
        },
        data() {
            let isPreOrder = false;
            let currentOffers = this.product.offers.filter((offer) => offer.available);

            if (currentOffers.length === 0) {
                // проверка на предзаказ
                currentOffers = this.product.offers.filter((offer) => !offer.available);
                isPreOrder = true;
            }

            const previewImage =
                currentOffers[0].previewImage?.medium || this.product.previewImage?.medium;

            return {
                currentOffers,
                offer: currentOffers[0],
                offerId: currentOffers[0].id,
                previewImage,
                price: currentOffers[0].price,
                discountPrice: currentOffers[0].discountPrice,
                discountPercent: currentOffers[0].discountPercent,
                inCart: this.product.inCart || false,
                loading: false,
                isPreOrder,
                previewImageFromOffer: "",
            };
        },
        watch: {
            inCartList() {
                this.checkProductInCart();
            },
            offerId() {
                this.checkProductInCart();
            },
        },
        computed: {
            nameProduct() {
                return this.product.shortName ? this.product.shortName : this.product.name;
            },
            scrImage() {
                return this.previewImageFromOffer ? apiUrl(this.previewImageFromOffer) : "";
            },
            isDiscount() {
                return this.discountPercent;
            },
            productPath() {
                return {
                    name: "Product",
                    params: { slug: this.product.slug },
                };
            },
        },
        mounted() {
            this.checkProductInCart();
        },
        methods: {
            apiUrl,
            checkProductInCart() {
                this.inCartList
                    ? (this.inCart = this.inCartList.includes(this.offerId.toString()))
                    : (this.inCart = false);
            },
            sendDataLayerEvent() {
                sendAnalytics.send(sendAnalytics.events.productClick, {
                    name: this.product.name,
                    offerId: this.offerId,
                    price: this.discountPrice,
                    position: this.position,
                });
            },
        },
    };
</script>

<template>
    <div class="product-card">
        <div class="product-card__img">
            <div v-if="isDiscount" class="product-card__sale-label">
                -{{ discountPercent }}%
            </div>

            <router-link
                :to="productPath"
                class="product-card__img-link js-lazy-load"
                tabindex="-1"
                @click.native.prevent="sendDataLayerEvent"
            >
                <img
                    class="product-card__img-product"
                    :data-src="apiUrl(previewImage)"
                    :src="scrImage"
                    alt=""
                />
                <div class="product-card__preloader">
                    <preloader :show="true" lg-size is-color></preloader>
                </div>
            </router-link>
        </div>

        <router-link
            :to="productPath"
            class="product-card__header"
            @click.native.prevent="sendDataLayerEvent"
        >
            <div class="product-card__name" :class="{ 'is-small': isSmall }">
                {{ nameProduct }}
            </div>
        </router-link>

        <div class="product-card__footer">
            <div class="product-card__price">
                <product-price
                    :price="discountPrice"
                    :old-price="price"
                    :discount-count="discountPercent"
                    is-small-size
                ></product-price>
            </div>
            <product-card-buttons
                size-button="lg"
                :offer-id="offerId"
                :product="product"
                :is-pre-order="isPreOrder"
                :in-cart="inCart"
                :discount-price="discountPrice"
                @on-added-to-cart="inCart = true"
            ></product-card-buttons>
        </div>
    </div>
</template>

<style lang="scss">
    .product-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: auto;
        padding: 6px 8px 12px;
        background-color: #fff;
        border: 2px solid var(--primary-border);
        border-radius: var(--border-lg);
        transition: border-color 0.2s ease-in;

        @include bp($bp-desktop-sm-for-card) {
            &:hover,
            &:focus-within {
                border-color: #ececec;
                .product-card__name {
                    color: var(--primary);
                }

                .product-card__img-link {
                    img {
                        transform: scale(1);
                    }
                }
            }
        }
    }

    .product-card__img {
        position: relative;
        z-index: 2;
        overflow: hidden;
    }

    .product-card__preloader {
        opacity: 1;
    }

    .product-card__sale-label {
        position: absolute;
        z-index: 3;
        left: 0;
        bottom: 6px;
        @include box(36px);
        color: #fff;
        font-size: 12px;
        font-weight: bold;
        line-height: 38px;
        text-align: center;
        border-radius: 50%;
        background-color: var(--danger);

        @include bp($bp-desktop-sm) {
            bottom: 10px;
            @include box(40px);
            font-size: 14px;
            line-height: 42px;
        }
    }

    .product-card__img-link {
        position: relative;
        display: block;
        padding-bottom: 100%;
        outline-offset: -1px;
        outline: none;

        img {
            @include center-absolute(0);
            margin: auto;
            max-height: 100%;
            transform: scale(0.98);
            transition: 0.3s ease-in-out transform;
        }
    }

    .product-card__img-product {
        z-index: 1;
        display: none;
        animation: fadeIn 0.3s ease-in-out;

        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: scale(0.9);
            }
            to {
                opacity: 1;
                transform: scale(0.98);
            }
        }

        &[lazy="loaded"] {
            display: block;
            & + .product-card__preloader {
                opacity: 0;
            }
        }
    }

    .product-card__price {
        display: block;
        margin-bottom: 12px;
    }

    .product-card__header {
        margin-top: 10px;
        outline: none;
    }

    .product-card__footer {
        margin-top: auto;
    }

    .product-card__name {
        display: block;
        position: relative;
        z-index: 2;
        font-size: 14px;
        line-height: 22px;
        font-weight: 500;
        color: var(--primary-text);
        margin-bottom: 5px;
        transition: 0.3s ease-in-out all;
        &.is-small {
            font-size: 14px;
            line-height: 18px;
        }

        @include bp($bp-desktop-sm) {
            font-size: 16px;
        }
    }

    .product-card__feature {
        display: block;
        margin-bottom: 14px;
        outline: none;
    }

    .product-card__controls {
        margin-top: auto;
    }

    .product-card__z-top {
        position: relative;
        z-index: 4;
    }
</style>
